import './styles.scss';

import { SortDirectionEnum } from '@/models/gen/graphql';
import { Col, Container, Row } from 'react-bootstrap';
import { Dispatch, SetStateAction } from 'react';
import { TODAY, TODAY_EOD } from '@/constants';
import { refetchAccidentIssueWithDocuments } from './api/services/searchAccidentIssue';

import AccidentHistoryFilters from '@/features/AccidentHistory/components/AccidentHistoryFilters';
import AccidentHistoryTable from '@/features/AccidentHistory/components/AccidentHistoryTable';
import { AccidentIssueWithDocuments } from './components/AccidentHistoryForm/types';
import Feature from '@/common/Feature';
import { useProvider } from '@/common/Provider';
import { ServiceIssueFiltersType } from '@/hooks/useServiceIssueFilters';

const AccidentHistory = (): JSX.Element => {
  return (
    <Feature name="AccidentHistory" context={initAccidentHistoryState} methods={incidentHistoryMethodConstructor}>
      <AccidentHistoryFilters />
      <Container>
        <Row>
          <Col>
            <AccidentHistoryTable />
          </Col>
        </Row>
      </Container>
    </Feature>
  );
};

export type AccidentHistoryFiltersType = {
  startDatetime: string;
  endDatetime: string;
  airportCode: string[];
  tripServicerIataAirlineCodes: string[];
  anyoneInjured: string;
};

export type AccidentHistoryRow = AccidentIssueWithDocuments;
export type AccidentHistoryContext = {
  filters: ServiceIssueFiltersType;
  accidents: Map<string, AccidentHistoryRow>;
  totalAccidents: number;
  selected: Map<string, AccidentHistoryRow>;
  loading: boolean;
  sortBy: string;
  sortDirection: SortDirectionEnum;
  search: string;
};
export type AccidentHistoryMethods = {
  resetState: (overrides?: Partial<AccidentHistoryContext>) => void;
  submitFilters: (values?: Partial<AccidentHistoryFiltersType>) => Promise<void>;
};

export const initAccidentHistoryState: AccidentHistoryContext = {
  filters: {
    startDatetime: TODAY,
    endDatetime: TODAY_EOD,
    airportCode: [],
    tripServicerIataAirlineCodes: [],
    anyoneInjured: null,
  },
  search: '',
  accidents: new Map(),
  totalAccidents: 0,
  selected: new Map(),
  loading: false,
  sortBy: undefined,
  sortDirection: SortDirectionEnum.Asc,
};
export const incidentHistoryMethodConstructor = (
  state: AccidentHistoryContext,
  setState: Dispatch<SetStateAction<AccidentHistoryContext>>
): AccidentHistoryMethods => ({
  resetState: (overrides?: Partial<AccidentHistoryContext>): void => setState({ ...initAccidentHistoryState, ...(overrides || {}) }),
  submitFilters: async (values: ServiceIssueFiltersType = state.filters): Promise<void> => {
    try {
      console.log(values);
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await refetchAccidentIssueWithDocuments([values]);
      setState((current) => {
        const accidents = new Map<string, AccidentHistoryRow>(res.rows.map((issue) => [issue.id, issue]));
        const totalAccidents = res.totalCount;
        return { ...current, accidents, totalAccidents };
      });
    } catch (err) {
      console.error('Error submitting filters:', err);
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  },
});
export function useAccidentHistory<T = AccidentHistoryContext>(
  selector?: (state: AccidentHistoryContext) => T
): [T, Dispatch<SetStateAction<AccidentHistoryContext>>, AccidentHistoryMethods] {
  return useProvider<AccidentHistoryContext, T, AccidentHistoryMethods>(selector);
}

export default AccidentHistory;
